import { NgModule } from '@angular/core';
import {
  RouterModule,
  Routes,
} from '@angular/router';
import { RedirectService } from '@pwc-bei/common';
import {
  AuthenticationGuard,
  AuthorizationGuard,
  AutologinGuard,
  PasswordManagerGuard,
} from '@pwc-bei/security';
import { LayoutComponent } from './components/layout/layout.component';
import { SocietySelectionComponent } from './components/society-selection/society-selection.component';
import { TermsConditionsComponent } from './components/terms-conditions/terms-conditions.component';
import { RoutePath } from './enums/route-path.enum';
import { AuthorityCodeEnum } from './enums/security/authority-code.enum';
import { FirstLoginGuard } from './first-login.guard';
import { ProfileRequiredGuard } from './guard/profile-required-guard';
import { TermsAndConditionsGuard } from './guard/terms-and-conditions-guard';
import { LegalRepresentativeGuard } from './legal-representative.guard';
import { BazarComponent } from './pages/bazar/pages/bazar/bazar.component';
import { RedirectToB2bComponent } from './pages/bazar/pages/redirect-to-b2b/redirect-to-b2b.component';
import { DelegationConfirmedComponent } from './pages/delegation-confirmed/delegation-confirmed.component';
import { ChangePasswordComponent } from './pages/profile/components/change-password/change-password.component';
import { UserManualComponent } from './pages/user-manual/user-manual.component';
import { CallFaqListComponent } from './pages/bandi/faq/components/callFaq-list.component';
import { SamlLoginComponent } from './components/saml-login/pages/saml-login.component';
import { SpecificTermsConditionsComponent } from './components/specific-terms-conditions/specific-terms-conditions.component';

/* ----- GUARDS ----- */
/* const firstLoginGuard = mapToCanActivate([FirstLoginGuard]);
const firstLoginGuardChild = mapToCanActivateChild([FirstLoginGuard]);
const profileRequiredGuard = mapToCanActivate([ProfileRequiredGuard]);
const profileRequiredGuardChild = mapToCanActivateChild([ProfileRequiredGuard]);
const termsAndConditionsGuard = mapToCanActivate([TermsAndConditionsGuard]);
const termsAndConditionsGuardChild = mapToCanActivateChild([TermsAndConditionsGuard]);
const legalRepresentativeGuard = mapToCanActivate([LegalRepresentativeGuard]);
const legalRepresentativeGuardChild = mapToCanActivateChild([LegalRepresentativeGuard]);
const authenticationGuard = mapToCanActivate([AuthenticationGuard]);
const authenticationGuardChild = mapToCanActivateChild([AuthenticationGuard]);
const authorizationGuard = mapToCanActivate([AuthorizationGuard]);
const authorizationGuardChild = mapToCanActivateChild([AuthorizationGuard]);
const autologinGuard = mapToCanActivate([AutologinGuard]);
const autologinGuardChild = mapToCanActivateChild([AutologinGuard]);
const passwordManagerGuard = mapToCanActivate([PasswordManagerGuard]);
const passwordManagerGuardChild = mapToCanActivateChild([PasswordManagerGuard]);*/

const signupRoute = [
  {
    path: RoutePath.SIGN_UP,
    loadChildren: () =>
      import('./pages/authentication/signup/signup.module').then(
        (m) => m.SignupModule,
      ),
  },
];

const delegationRoute = [
  {
    path: RoutePath.DELEGATE,
    canActivate: [FirstLoginGuard, LegalRepresentativeGuard],
    loadChildren: () =>
      import('./pages/delegation/delegation-module').then(
        (m) => m.DelegationModule,
      ),
  },
];

const routes: Routes = [
  {
    path: RoutePath.LOGIN,
    loadChildren: () =>
      import('./pages/authentication/login/login.module').then(
        (m) => m.LoginModule,
      ),
  },
  {
    path: RoutePath.AUTH + RoutePath.SLASH + RoutePath.SSO,
    component: SamlLoginComponent
  },
  {
    path: RoutePath.LOGIN + RoutePath.SLASH + ':token',
    loadChildren: () =>
      import('./pages/authentication/login/login.module').then(
        (m) => m.LoginModule,
      ),
  },
  {
    path: RoutePath.FORGOT_PASSWORD,
    loadChildren: () =>
      import(
        './pages/authentication/forgot-password/forgot-password.module'
      ).then((m) => m.ForgotPasswordModule),
  },
  ...signupRoute,
  {
    path: RoutePath.RESET_PASSWORD + RoutePath.SLASH + ':token',
    loadChildren: () =>
      import(
        './pages/authentication/reset-password/reset-password.module'
      ).then((m) => m.ResetPasswordModule),
  },
  {
    path: RoutePath.WELCOME_FIRST_LOGIN + RoutePath.SLASH + ':token',
    loadChildren: () =>
      import('./pages/authentication/first-login/first-login.module').then(
        (m) => m.FirstLoginModule,
      ),
  },
  {
    path: RoutePath.TERMS_AND_CONDITIONS,
    canActivate: [AuthenticationGuard],
    component: TermsConditionsComponent,
  },
  {
    path: RoutePath.TERMS_AND_CONDITIONS + RoutePath.SLASH + ':termsID',
    canActivate: [
      AutologinGuard,
      AuthenticationGuard,
      AuthorizationGuard,
      PasswordManagerGuard,
      TermsAndConditionsGuard,
    ],
    component: SpecificTermsConditionsComponent,
  },
  {
    path: RoutePath.B2B,
    canActivate: [AuthenticationGuard],
    component: RedirectToB2bComponent,
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [
      AutologinGuard,
      AuthenticationGuard,
      AuthorizationGuard,
      PasswordManagerGuard,
      TermsAndConditionsGuard,
    ],
    canActivateChild: [
      AutologinGuard,
      AuthenticationGuard,
      PasswordManagerGuard,
      TermsAndConditionsGuard,
    ],
    children: [
      {
        path: RoutePath.HOME,
        canActivate: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        canActivateChild: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        component: BazarComponent,
        data: {
          authorities: [AuthorityCodeEnum.ROLE_BANDI_B2C_ACCESS],
          redirect: [RoutePath.B2B],
        },
      },
      {
        path: '',
        redirectTo: `/${RoutePath.REDIRECT}?url=${window.origin}/${RoutePath.HOME}&=_self`,
        canActivate: [FirstLoginGuard],
        pathMatch: 'full',
      },
      {
        path: RoutePath.USER_PROFILE,
        loadChildren: () =>
          import('./pages/user-profile/user-profile.module').then(
            (m) => m.UserProfileModule,
          ),
      },
      {
        path: RoutePath.SERVICE_REQUESTS_LIST,
        canActivate: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        canActivateChild: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        loadChildren: () =>
          import('./pages/service-requests/service-requests.module').then(
            (m) => m.ServiceRequestsModule,
          ),
      },
      {
        path: RoutePath.HOME + RoutePath.SLASH + RoutePath.EDUCATION_AREA,
        canActivateChild: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
          FirstLoginGuard,
        ],
        loadChildren: () => import('./pages/bandi/educatio-area/education-area.module').then(
          m => m.EducationAreaModule
        )
      },
      {
        path: RoutePath.HOME + RoutePath.SLASH + RoutePath.GLOSSARY,
        canActivateChild: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        loadChildren: () => import('./pages/bandi/glossary/glossary.module').then(
          m => m.GlossaryModule
        )
      },
      {
        path: RoutePath.HOME + RoutePath.SLASH + RoutePath.FAQ,
        canActivateChild: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        component: CallFaqListComponent
        // loadChildren: () => import('./pages/bandi/faq/components/callFaq-list.component').then(
        //   m => m.CallFaqListComponent
        // )
      },
      {
        path: RoutePath.LIVE_ASSISTANT,
        canActivateChild: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        loadChildren: () =>
          import('./pages/live-assistant/live-assistant.module').then(
            (m) => m.LiveAssistantModule,
          ),
      },
      {
        path: RoutePath.RDP,
        loadChildren: () =>
          import('./pages/rdp/rdp.module').then((m) => m.RdpModule),
      },
      {
        path: RoutePath.POINT_AND_CLICK + RoutePath.SLASH + ':id',
        loadChildren: () =>
          import('./pages/point-&-click/point-and-click.module').then(
            (m) => m.PointAndClickModule,
          ),
      },
      {
        path: RoutePath.SERVICE_POINT_AND_CLICK + RoutePath.SLASH + ':id',
        loadChildren: () =>
          import(
            './pages/service-point-and-click/service-point-and-click.module'
          ).then((m) => m.ServicePointAndClickModule),
      },
      ...delegationRoute,
      {
        path:
          RoutePath.DELEGATION_CONFIRMED +
          RoutePath.SLASH +
          ':id' +
          RoutePath.SLASH +
          ':token',
        canActivate: [AuthenticationGuard],
        component: DelegationConfirmedComponent,
      },
      {
        path: RoutePath.BILLING_DATA,
        loadChildren: () =>
          import('./pages/invoice-data-rdp/invoice-data-rdp.module').then(
            (m) => m.InvoiceDataRdpModule,
          ),
      },
      {
        path: RoutePath.SOCIETY_SELECTION,
        canActivate: [AuthenticationGuard],
        component: SocietySelectionComponent,
      },
      {
        path: RoutePath.CHANGE_PASSWORD,
        canActivate: [AuthenticationGuard],
        component: ChangePasswordComponent,
      },
      {
        path: RoutePath.INTRODUCTION,
        canActivate: [AuthenticationGuard], // TODO : ADD ONLY DIRECT CHANNEL AUTHORIZATION
        component: UserManualComponent,
      },
      {
        path: RoutePath.THEMATIC_AREA,
        canActivate: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        canActivateChild: [
          AutologinGuard,
          AuthenticationGuard,
          AuthorizationGuard,
          PasswordManagerGuard,
          TermsAndConditionsGuard,
          ProfileRequiredGuard,
        ],
        loadChildren: () =>
          import('./pages/thematic-area/thematic-area.module').then(
            (m) => m.ThematicAreaModule,
          ),
      },
      {
        path: RoutePath.BANDI,
        loadChildren: () =>
          import('./pages/bandi/bandi.module').then((m) => m.BandiModule),
        canActivate: [FirstLoginGuard],
      },
      {
        path: RoutePath.INCENTIVI,
        loadChildren: () =>
          import('./pages/incentivi/incentivi.module').then(
            (m) => m.IncentiviModule,
          ),
          canActivate: [FirstLoginGuard],
      },
    ],
  },
  {
    path: RoutePath.ACCOUNT + RoutePath.SLASH + RoutePath.CHANGE_PASSWORD,
    canActivate: [AuthenticationGuard],
    loadChildren: () =>
      import('./pages/authentication/first-login/first-login.module').then(
        (m) => m.FirstLoginModule,
      ),
  },
  {
    path: RoutePath.B2B,
    canActivate: [AuthenticationGuard, AuthorizationGuard],
    redirectTo: `/${RoutePath.REDIRECT}?url=${window.origin}&=_self`,
    pathMatch: 'full',
  },
  {
    path: RoutePath.REDIRECT,
    canActivate: [RedirectService],
    loadChildren: () =>
      import('./pages/authentication/login/login.module').then(
        (m) => m.LoginModule,
      ),
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
